// Funnel instrumentation on top of the GA4 tag loaded in public/index.html.
//
// GA4 was previously collecting pageviews only — no funnel step was measurable.
// Every event name used anywhere in the app must be declared in EVENTS below and
// documented in docs/ANALYTICS_EVENTS.md.
//
// Rules:
//   - Never pass PII (email, name, uploaded content) as an event param.
//   - Events are no-ops when gtag is absent (ad blockers, local dev, prerender).

export const EVENTS = {
    // Acquisition / landing
    CTA_CLICK: "cta_click",
    PRICING_VIEW: "pricing_view",
    PLAN_SELECT: "plan_select",
    FAQ_OPEN: "faq_open",
    DEMO_OPENED: "demo_opened",

    // Signup
    SIGNUP_STARTED: "signup_started",
    SIGNUP_COMPLETED: "signup_completed",
    SIGNUP_FAILED: "signup_failed",

    // Monetization
    CHECKOUT_INITIATED: "checkout_initiated",
};

// react-snap prerenders routes in a headless browser at build time. Without this
// guard every mount-time event would be recorded as a real session on each build.
const isPrerender = () =>
    typeof navigator !== "undefined" && /ReactSnap/i.test(navigator.userAgent || "");

const canTrack = () =>
    typeof window !== "undefined" && typeof window.gtag === "function" && !isPrerender();

/**
 * Send a GA4 event. Silent no-op when analytics is unavailable — instrumentation
 * must never be able to break a user flow.
 */
export const track = (event, params = {}) => {
    if (!canTrack()) return;
    try {
        window.gtag("event", event, params);
    } catch (err) {
        // Swallow: a failed beacon is never worth surfacing to the user.
    }
};

/**
 * @param {string} location - where the CTA lives, e.g. "hero", "nav", "final_cta"
 * @param {string} label    - the action, e.g. "start_trial", "see_pricing"
 */
export const trackCta = (location, label, params = {}) =>
    track(EVENTS.CTA_CLICK, { cta_location: location, cta_label: label, ...params });

/**
 * @param {string} plan    - plan slug: starter | basic | pro
 * @param {string} billing - "monthly" | "annual"
 */
export const trackPlanSelect = (plan, billing, location = "landing_pricing") =>
    track(EVENTS.PLAN_SELECT, { plan, billing, cta_location: location });

export const trackCheckoutInitiated = (plan, billing, value) =>
    track(EVENTS.CHECKOUT_INITIATED, { plan, billing, value, currency: "USD" });
